import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DASHBOARD_SUMMARY_PERIODS, type DashboardSummaryPeriod } from '@/types/shared';
import './DashboardFilterBar.css';

/** `DashboardSummaryPeriod` -> its pill label i18n key (`13-dashboard-restyle-spec.md §3`'s
 * اليوم/هذا الشهر/هذه السنة/مخصص pills, made functional per T97/T100). */
const PERIOD_LABEL_KEY: Record<DashboardSummaryPeriod, string> = {
  today: 'dashboard.filterBar.periods.today',
  'this-week': 'dashboard.filterBar.periods.thisWeek',
  'this-month': 'dashboard.filterBar.periods.thisMonth',
  'this-year': 'dashboard.filterBar.periods.thisYear',
  custom: 'dashboard.filterBar.periods.custom',
};

export interface DashboardFilterBarFieldErrors {
  from?: string;
  to?: string;
}

interface DashboardFilterBarProps<P extends DashboardSummaryPeriod = DashboardSummaryPeriod> {
  /** Which period pills to render (in order). Defaults to all DASHBOARD_SUMMARY_PERIODS; a consumer
   * with a narrower set (e.g. the finance page, which has no `this-week`) passes its own list. */
  periods?: readonly P[];
  /** `null` = no period selected (the default, unfiltered view) — no pill is `aria-pressed`. */
  selectedPeriod: P | null;
  /** Re-clicking the already-active pill is how the user gets back to the default view — the
   * parent owns that toggle-off decision (it also owns the resulting refetch). */
  onPillClick: (period: P) => void;
  customFrom: string;
  customTo: string;
  onCustomFromChange: (value: string) => void;
  onCustomToChange: (value: string) => void;
  onApply: () => void;
  /** `true` while a summary fetch triggered by this bar (period pill or Apply) is in flight. */
  isBusy: boolean;
  fieldErrors: DashboardFilterBarFieldErrors;
  generalError?: string | null;
}

/**
 * The Admin dashboard's period filter bar (T97/T98/T100) — functional pill-tabs
 * (اليوم/هذا الشهر/هذه السنة/مخصص) + a custom Cairo date range, Apply-gated. Opt-in: nothing here
 * fires a request on mount, only on explicit interaction (`AdminDashboardGrid` owns the actual
 * fetch — this component is presentational).
 */
export function DashboardFilterBar<P extends DashboardSummaryPeriod = DashboardSummaryPeriod>({
  periods,
  selectedPeriod,
  onPillClick,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
  onApply,
  isBusy,
  fieldErrors,
  generalError,
}: DashboardFilterBarProps<P>) {
  const { t } = useTranslation();

  const periodList = periods ?? (DASHBOARD_SUMMARY_PERIODS as unknown as readonly P[]);

  function handleApplySubmit(e: FormEvent) {
    e.preventDefault();
    onApply();
  }

  return (
    <div className="fa-dashboard-filter-bar">
      <div
        className="fa-dashboard-filter-bar__pills"
        role="group"
        aria-label={t('dashboard.filterBar.groupLabel')}
      >
        {periodList.map((period) => (
          <button
            key={period}
            type="button"
            className="fa-dashboard-filter-bar__pill"
            aria-pressed={selectedPeriod === period}
            onClick={() => onPillClick(period)}
          >
            {t(PERIOD_LABEL_KEY[period])}
          </button>
        ))}
      </div>

      {selectedPeriod === 'custom' && (
        <form
          className="fa-dashboard-filter-bar__custom"
          onSubmit={handleApplySubmit}
          aria-label={t('dashboard.filterBar.periods.custom')}
        >
          <div className="fa-dashboard-filter-bar__field">
            <label htmlFor="dashboard-filter-from">
              {t('dashboard.filterBar.customFromLabel')}
            </label>
            <input
              id="dashboard-filter-from"
              type="date"
              value={customFrom}
              onChange={(e) => onCustomFromChange(e.target.value)}
              aria-invalid={Boolean(fieldErrors.from)}
              aria-describedby={fieldErrors.from ? 'dashboard-filter-from-error' : undefined}
            />
            {fieldErrors.from && (
              <p
                id="dashboard-filter-from-error"
                className="fa-dashboard-filter-bar__field-error"
                role="alert"
              >
                {fieldErrors.from}
              </p>
            )}
          </div>

          <div className="fa-dashboard-filter-bar__field">
            <label htmlFor="dashboard-filter-to">{t('dashboard.filterBar.customToLabel')}</label>
            <input
              id="dashboard-filter-to"
              type="date"
              value={customTo}
              onChange={(e) => onCustomToChange(e.target.value)}
              aria-invalid={Boolean(fieldErrors.to)}
              aria-describedby={fieldErrors.to ? 'dashboard-filter-to-error' : undefined}
            />
            {fieldErrors.to && (
              <p
                id="dashboard-filter-to-error"
                className="fa-dashboard-filter-bar__field-error"
                role="alert"
              >
                {fieldErrors.to}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="fa-btn fa-btn--primary fa-dashboard-filter-bar__apply"
            disabled={isBusy}
          >
            {isBusy ? t('dashboard.filterBar.applying') : t('dashboard.filterBar.apply')}
          </button>

          {generalError && (
            <p className="fa-dashboard-filter-bar__general-error" role="alert">
              {generalError}
            </p>
          )}
        </form>
      )}
    </div>
  );
}
